/**
 * GovRAMP Core submission package — V103-7-4 (D-103-8; dogfood exit V103-8-7).
 *
 * The eight items GovRAMP's Core submission requires, each with a status, the
 * signed evidence that satisfies it and an action that creates or uploads it
 * (no dead ends):
 *
 *   ssp                 the signed `govramp_ssp_xlsx` for this period
 *   diagrams            approved documents linked to the PL-2 objectives (boundary / data-flow diagrams)
 *   policies_procedures published policies (Policies module)
 *   user_guide          published policy `user_guide` (template: V103-7-6)
 *   rules_of_behavior   published policy `rules_of_behavior`
 *   plans_catalog       published `plans_catalog` + CM, IR and contingency plans
 *   conmon_90_day       signed `govramp_conmon_bundle`s covering at least 90 days (each carries the POA&M)
 *   laws_regulations    published policy `laws_and_regulations`
 *
 * Assembling signs a `govramp_core_submission` package that names each item's
 * evidence by id + sha256 (packages verify independently at /verify). An
 * incomplete checklist is refused unless the caller asks for a dry run.
 */
import { GOVRAMP_PLAN_TEMPLATES } from '../data/govramp-plan-templates.js';
import { issueCertificationPackage, listCertificationPackages } from './certification-package-service.js';
import { listPublishedPolicies } from './policy-ack-service.js';
import { GovrampWorkbookError, govrampPackagePeriod } from './govramp-ssp-workbook-service.js';
export const CONMON_REQUIRED_DAYS = 90;
const PLAN_KEYS = new Set(GOVRAMP_PLAN_TEMPLATES.map((t) => t.key));
const CATALOG_PLANS = ['plans_catalog', 'configuration_management_plan', 'incident_response_plan', 'contingency_plan'];
/** The eight required items for a GovRAMP period (workspace-scoped). */
export async function buildCoreSubmissionChecklist(db, input) {
    const period = await govrampPackagePeriod(db, input.workspaceId, input.periodId, 'govramp_core_submission');
    const program = encodeURIComponent(period.program);
    const q = `period=${encodeURIComponent(period.period_id)}`;
    const api = (p) => ({ method: 'POST', path: `/api/w/:slug/programs/${program}/packages/${p}` });
    const packages = await listCertificationPackages(db, input.workspaceId, period.profile_id);
    const pkgEvidence = (p) => ({ kind: 'package', id: p.package_id, sha256: p.content_sha256, title: p.kind, at: p.created_at });
    const items = [];
    // 1. SSP workbook for this period.
    const ssp = packages.find((p) => p.kind === 'govramp_ssp_xlsx' && p.period_start === period.period_start && p.period_end === period.period_end);
    items.push({
        key: 'ssp', label: 'System Security Plan (Controls Matrix workbook)', status: ssp ? 'complete' : 'missing',
        detail: ssp ? `Signed ${ssp.created_at.slice(0, 10)}.` : 'Not generated for this period.',
        evidence: ssp ? [pkgEvidence(ssp)] : [],
        action: { key: 'generate_ssp_workbook', label: ssp ? 'Regenerate the SSP workbook' : 'Generate the SSP workbook', href: `/programs/${program}/report?${q}`, api: { ...api('ssp-workbook') } },
    });
    // 2. Diagrams: approved documents linked to PL-2 objectives.
    const docs = (await db.query(`SELECT l.link_id, l.artifact_id, l.artifact_sha256, l.approved_at, a.title
       FROM document_objective_links l
       JOIN evidence_artifacts a ON a.artifact_id = l.artifact_id
       JOIN customer_profiles cp ON cp.profile_id = a.profile_id AND cp.workspace_id = l.workspace_id
      WHERE l.workspace_id = ? AND l.ended_at IS NULL AND (l.objective_id LIKE 'nist-800-53:pl-2\\_%' ESCAPE '\\' OR l.objective_id = 'nist-800-53:pl-2')
      ORDER BY l.approved_at DESC, l.link_id`, [input.workspaceId])).results;
    const seen = new Set();
    const diagramEvidence = docs.filter((d) => !seen.has(d.artifact_id) && seen.add(d.artifact_id))
        .map((d) => ({ kind: 'document', id: d.artifact_id, sha256: d.artifact_sha256, title: d.title ?? d.artifact_id, at: d.approved_at }));
    items.push({
        key: 'diagrams', label: 'Authorization boundary and data-flow diagrams', status: diagramEvidence.length ? 'complete' : 'missing',
        detail: diagramEvidence.length ? `${diagramEvidence.length} approved diagram document(s).` : 'No approved diagram linked to PL-2.',
        evidence: diagramEvidence,
        action: { key: 'link_boundary_diagram', label: 'Upload and link a boundary diagram', href: `/programs/${program}/prepare?${q}&objective=nist-800-53:pl-2` },
    });
    // 3–6, 8: Policies module.
    const published = await listPublishedPolicies(db, input.workspaceId);
    const byKey = new Map(published.map((p) => [p.policy_key, p]));
    const polEvidence = (key) => {
        const p = byKey.get(key);
        return p ? [{ kind: 'policy', id: p.version_id, sha256: p.body_sha256, title: p.title, at: p.published_at ?? p.created_at }] : [];
    };
    const policyAction = (keys, label) => ({ key: 'publish_policy', label, href: `/policies?template=${encodeURIComponent(keys[0])}`, template_keys: keys });
    const general = published.filter((p) => !PLAN_KEYS.has(p.policy_key));
    items.push({
        key: 'policies_procedures', label: 'Policies and procedures', status: general.length ? 'complete' : 'missing',
        detail: general.length ? `${general.length} published polic${general.length === 1 ? 'y' : 'ies'}.` : 'No published policy yet.',
        evidence: general.map((p) => ({ kind: 'policy', id: p.version_id, sha256: p.body_sha256, title: p.title, at: p.published_at ?? p.created_at })),
        action: { key: 'publish_policy', label: 'Publish policies', href: '/policies' },
    });
    for (const [key, label] of [['user_guide', 'User guide'], ['rules_of_behavior', 'Rules of behavior']]) {
        const ev = polEvidence(key);
        items.push({ key, label, status: ev.length ? 'complete' : 'missing', detail: ev.length ? `Published v${byKey.get(key).version}.` : 'Not published.', evidence: ev,
            action: policyAction([key], ev.length ? `Update the ${label.toLowerCase()}` : `Start from the ${label.toLowerCase()} template`) });
    }
    const catalog = CATALOG_PLANS.flatMap(polEvidence);
    const missingPlans = CATALOG_PLANS.filter((k) => !byKey.has(k));
    items.push({
        key: 'plans_catalog', label: 'Plans catalog (CM, IR and contingency plans)',
        status: missingPlans.length === 0 ? 'complete' : catalog.length ? 'partial' : 'missing',
        detail: missingPlans.length ? `Not published: ${missingPlans.join(', ')}.` : 'Catalog and the three plans are published.',
        evidence: catalog, action: policyAction(missingPlans.length ? missingPlans : CATALOG_PLANS, 'Publish the plans from their templates'),
    });
    // 7. 90 days of ConMon data (each signed bundle carries the POA&M).
    const bundles = packages.filter((p) => p.kind === 'govramp_conmon_bundle');
    let start = null;
    let end = null;
    const bundleEvidence = [];
    for (const b of bundles) {
        const row = (await db.query(`SELECT content_json FROM certification_packages WHERE package_id = ? AND workspace_id = ? LIMIT 1`, [b.package_id, input.workspaceId])).results[0];
        let w = {};
        try {
            w = JSON.parse(row?.content_json ?? '{}').window ?? {};
        }
        catch {
            w = {};
        }
        if (w.start && (!start || w.start < start))
            start = w.start;
        if (w.end && (!end || w.end > end))
            end = w.end;
        bundleEvidence.push(pkgEvidence(b));
    }
    const days = start && end ? Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000) : 0;
    items.push({
        key: 'conmon_90_day', label: '90 days of ConMon data and the POA&M',
        status: bundles.length === 0 ? 'missing' : days >= CONMON_REQUIRED_DAYS ? 'complete' : 'partial',
        detail: bundles.length ? `${bundles.length} signed bundle(s) covering ${days} day(s) (${start} → ${end}).` : 'No signed ConMon bundle yet.',
        evidence: bundleEvidence,
        action: { key: 'generate_conmon_bundle', label: 'Generate the ConMon bundle for a cycle', href: `/programs/${program}/operate?${q}`, api: { ...api('conmon-bundle') } },
    });
    const laws = polEvidence('laws_and_regulations');
    items.push({ key: 'laws_regulations', label: 'Applicable laws and regulations', status: laws.length ? 'complete' : 'missing',
        detail: laws.length ? `Published v${byKey.get('laws_and_regulations').version}.` : 'Not published.', evidence: laws,
        action: policyAction(['laws_and_regulations'], 'Start from the laws and regulations template') });
    // Order as GovRAMP's package folders: SSP, diagrams, policies, (procedures), user guide, rules, plans, ConMon, laws.
    const order = ['ssp', 'diagrams', 'policies_procedures', 'user_guide', 'rules_of_behavior', 'plans_catalog', 'conmon_90_day', 'laws_regulations'];
    items.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
    return { profile_id: period.profile_id, period_id: period.period_id, complete: items.every((i) => i.status === 'complete'), items };
}
/** Sign the Core submission package (refused while incomplete unless `allowIncomplete`, a dry run). */
export async function issueGovrampCoreSubmission(db, input) {
    const now = input.now ?? new Date();
    const checklist = await buildCoreSubmissionChecklist(db, { workspaceId: input.workspaceId, periodId: input.periodId, now });
    if (!checklist.complete && !input.allowIncomplete) {
        throw new GovrampWorkbookError('conflict', 'The Core submission is incomplete; finish the open items or assemble a dry run.', { code: 'incomplete', missing: checklist.items.filter((i) => i.status !== 'complete').map((i) => i.key) });
    }
    const period = await govrampPackagePeriod(db, input.workspaceId, input.periodId, 'govramp_core_submission');
    const pkg = await issueCertificationPackage(db, {
        workspace_id: input.workspaceId, profile_id: period.profile_id, kind: 'govramp_core_submission', package_id: input.packageId, sign: input.sign,
        created_by_user_id: input.createdByUserId, period_start: period.period_start, period_end: period.period_end, now,
        rules_version: typeof period.options.tier === 'string' ? `govramp:${period.options.tier}` : null,
        content: {
            schema: 'gacpro/govramp-core-submission@1', kind: 'govramp_core_submission', generated_at: now.toISOString(),
            program: period.program, tier: period.options.tier ?? null, impact_level: period.options.impact_level ?? null,
            period: { period_id: period.period_id, period_start: period.period_start, period_end: period.period_end, status: period.status },
            dry_run: !checklist.complete,
            items: checklist.items.map((i) => ({ key: i.key, label: i.label, status: i.status, evidence: i.evidence.map((e) => ({ kind: e.kind, id: e.id, sha256: e.sha256, at: e.at })) })),
        },
    });
    return { package: pkg, checklist };
}
//# sourceMappingURL=govramp-core-submission-service.js.map